"use client";

import type * as React from "react";
import { cn } from "@/lib/utils";

export interface PageHeaderProps {
  /** Optional uppercase eyebrow above the title. */
  eyebrow?: React.ReactNode;
  /** The page title. Rendered as an <h1> in the serif heading face. */
  title: React.ReactNode;
  /** Optional supporting paragraph below the title. */
  sub?: React.ReactNode;
  /** Optional right-aligned actions (buttons, links). */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * Calm Authority page header. Ports docs/design-system/templates/app/layout.jsx
 * `PageHeader`: an optional brand eyebrow, a large serif <h1>, an optional
 * sub-paragraph, and an optional right-aligned actions slot. All text is caller
 * supplied (already translated) — this component hardcodes no copy.
 */
export function PageHeader({
  eyebrow,
  title,
  sub,
  actions,
  className,
}: PageHeaderProps): React.ReactElement {
  return (
    <div
      className={cn("mb-6 flex items-end gap-5", className)}
      data-slot="page-header"
      data-test="page-header"
    >
      <div className="min-w-0 flex-1">
        {eyebrow ? (
          <div
            className="mb-2 font-bold text-[12px] uppercase tracking-[0.08em] text-[var(--brand-ink)]"
            data-test="page-header-eyebrow"
          >
            {eyebrow}
          </div>
        ) : null}
        <h1 className="font-heading font-semibold text-3xl leading-tight tracking-[-0.02em] text-foreground">
          {title}
        </h1>
        {sub ? (
          <p className="mt-2 max-w-[620px] text-[15px] leading-relaxed text-[var(--ink-2)]">
            {sub}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-2.5">{actions}</div>
      ) : null}
    </div>
  );
}
